import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, FLOOR_OPTIONS, PHONE_DISPLAY, PHONE_TEL, EMAIL_URL, EMAIL_DISPLAY } from "@/lib/contact";

const inputCls = "border-2 border-ink bg-white p-3 font-body text-base normal-case tracking-normal outline-none focus:border-transport";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return "Hiba történt. Kérjük, hívjon minket telefonon!";
};

const Success = ({ onReset }) => (
  <div data-testid="quote-success" className="grid gap-4 border-2 border-ink p-6 bg-ground text-ink content-start">
    <CheckCircle2 className="w-10 h-10 text-transport" aria-hidden="true" />
    <p className="mo text-2xl">Megkaptuk a kérését!</p>
    <p>Hamarosan visszahívjuk az árral. Ha sürgős, hívjon most:</p>
    <a href={PHONE_TEL} className="btn-press mo bg-signal text-ink py-4 text-center border-2 border-ink">{PHONE_DISPLAY}</a>
    <button type="button" onClick={onReset} data-testid="quote-new-request" className="lb underline justify-self-start">Új kérés küldése</button>
  </div>
);

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", floor: "" });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.floor) return toast.error("Kérjük, válassza ki az emeletet.");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (photo) fd.append("photo", photo);
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, fd);
      setDone(true);
      toast.success("Kérés elküldve! Hamarosan hívjuk.");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setForm({ name: "", phone: "", floor: "" }); setPhoto(null); setDone(false); };
  if (done) return <Success onReset={reset} />;

  return (
    <form onSubmit={submit} data-testid="quote-form" className="grid gap-4 border-2 border-ink p-6 bg-ground text-ink">
      <label className="grid gap-1 lb">Név
        <input data-testid="quote-name-input" required minLength={2} className={inputCls} type="text" autoComplete="name"
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </label>
      <label className="grid gap-1 lb">Telefonszám
        <input data-testid="quote-phone-input" required className={inputCls} type="tel" autoComplete="tel" placeholder="06 20 123 4567"
          value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </label>
      <div className="grid gap-1 lb">
        <span id="floor-label">Emelet / Van-e lift</span>
        <Select value={form.floor} onValueChange={(v) => setForm({ ...form, floor: v })}>
          <SelectTrigger aria-labelledby="floor-label" data-testid="quote-floor-select"
            className="h-auto rounded-none border-2 border-ink bg-white p-3 font-body text-base normal-case tracking-normal">
            <SelectValue placeholder="Válasszon…" />
          </SelectTrigger>
          <SelectContent className="rounded-none border-2 border-ink">
            {FLOOR_OPTIONS.map((o, i) => (
              <SelectItem key={o} value={o} data-testid={`quote-floor-option-${i}`}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <label className="grid gap-1 lb">Kép feltöltése (Opcionális)
        <input data-testid="quote-photo-input" className={`${inputCls} border-dashed`} type="file" accept="image/*"
          onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
      </label>
      <button type="submit" disabled={loading} data-testid="quote-submit-button"
        className="btn-press mo inline-flex items-center justify-center gap-2 bg-signal text-ink py-4 text-lg border-2 border-ink disabled:opacity-60">
        {loading && <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />}
        Kanapé Elszállítás Kérése
      </button>
    </form>
  );
};

export const QuoteSection = () => (
  <section id="ajanlat" data-testid="quote-section" className="bg-white text-ink py-24 md:py-32">
    <div id="contact" className="px-6 grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
      <div className="min-w-0">
        <p className="lb text-transport">Szállítólevél</p>
        <h2 className="mo mt-4">GYORS ÁRAJÁNLAT 1 PERC ALATT</h2>
        <p className="mt-6">Küldje el a kanapé adatait vagy képét, és azonnal mondjuk az árat!</p>
        <p className="mo mt-8 text-2xl">Küldjön fotót az űrlapon vagy e-mailben az azonnali árért!</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a data-testid="quote-email-link" href={EMAIL_URL} className="btn-press mo px-5 py-3 bg-transport text-white break-all">{EMAIL_DISPLAY}</a>
          <a data-testid="quote-call-link" href={PHONE_TEL} className="btn-press mo px-5 py-3 bg-ink text-white">{PHONE_DISPLAY}</a>
        </div>
      </div>
      <QuoteForm />
    </div>
  </section>
);
